import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  exportTableColumnValidation,
  tableColumnValidation,
  TableSettingColumn,
} from 'types/tableSettingsTypes';

interface AdminSearchTermsState {
  excludedFilter: string[];
  filterData: Record<string, any>;
  reloadTableData: boolean;
  tableColumnValidation: tableColumnValidation;
  defaultColumnList: TableSettingColumn[];
  tableColumnList: TableSettingColumn[];
  exportTableColumnValidation: exportTableColumnValidation;
  exportTableColumnList: TableSettingColumn[];
}
const defaultColumnList = [
  {
    name: 'search_location',
    label_key: 'search_location',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'select',
    filter_placeholder: 'search_location_all',
    selectKey: 'search_location',
    option_value_key: 'slug',
    option_label_key: 'name',
    multi: false,
    order: 1,
  },
  {
    name: 'keyword',
    label_key: 'keyword',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 2,
  },
  {
    name: 'original_keyword',
    label_key: 'original_keyword',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 3,
  },
  {
    name: 'total_search',
    label_key: 'total_search',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 4,
  },
  {
    name: 'last_search_at',
    label_key: 'last_search_at',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 5,
  },
  {
    name: 'created_at',
    label_key: 'created_at',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 6,
  },
];
const defaultConfig = {
  allow_list: true,
  allow_filter: false,
  show_list_checkbox: true,
  show_filter_checkbox: false,
};

const fields = [
  { key: 'search_location', label: 'search_location' },
  { key: 'keyword', label: 'keyword' },
  { key: 'original_keyword', label: 'original_keyword' },
  { key: 'total_search', label: 'total_search' },
  { key: 'last_search_at', label: 'last_search_at' },
  { key: 'created_at', label: 'created_at' },
];

const exportColumnList = fields.map((field, index) => ({
  name: field.key,
  label_key: field.label,
  order: index + 1,
  ...defaultConfig,
}));
const initialState: AdminSearchTermsState = {
  excludedFilter: ['search'],
  filterData: {},
  reloadTableData: false,
  tableColumnValidation: {
    minListColumn: 2,
    maxListColumn: 6,
    minFilterColumn: 0,
    maxFilterColumn: 6,
  },
  defaultColumnList: defaultColumnList,
  tableColumnList: JSON.parse(JSON.stringify(defaultColumnList)),
  exportTableColumnValidation: {
    minListColumn: 2,
  },
  exportTableColumnList: exportColumnList,
};

const adminSearchTermsSlice = createSlice({
  name: 'adminSearchTerms',
  initialState,
  reducers: {
    setFilterData(state, action: PayloadAction<Record<string, any>>) {
      state.filterData = action.payload;
    },
    removeFilterKey(state, action: PayloadAction<string>) {
      delete state.filterData[action.payload];
    },
    resetFilterData(state) {
      state.filterData = {};
    },
    setReloadTableData(state, action: PayloadAction<boolean>) {
      state.reloadTableData = action.payload;
    },
    setSearchTermsTableColumnList(state, action: PayloadAction<TableSettingColumn[]>) {
      state.tableColumnList = action.payload;
    },
  },
});

export const {
  setFilterData,
  removeFilterKey,
  resetFilterData,
  setReloadTableData,
  setSearchTermsTableColumnList,
} = adminSearchTermsSlice.actions;

export default adminSearchTermsSlice.reducer;
